/**
 * Usage days, read back as figures: the totals of a period, the same totals per
 * model and effort and per kind of work, and a value per day for the trend
 * lines. Pure — the desktop page and the account page both call it, and the
 * findings (`findings.ts`) are written from what it returns.
 */
import { parseBucketKey, sumDays, type StepKind, type UsageBucket, type UsageDay, type UsageDayRow } from './day';

export interface Totals {
  turns: number;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Micro-dollars at list price. */
  cost: number;
  activeMs: number;
  /** A task's outcomes — the `task` buckets. */
  started: number;
  landed: number;
  failed: number;
  stopped: number;
  reruns: number;
  /** A workflow step's — every other kind. */
  stepsPassed: number;
  stepsFailed: number;
  stepReruns: number;
  conflicts: number;
  landMs: number;
  landCount: number;
}

export interface PairRow extends Totals {
  model: string;
  effort: string;
}

export interface KindRow extends Totals {
  kind: StepKind;
}

export interface DayPoint {
  day: string;
  cost: number;
  turns: number;
  tokens: number;
  landed: number;
  started: number;
  reruns: number;
  conflicts: number;
  /** Time to land summed over the day's landings, and how many it sums. */
  landMs: number;
  landCount: number;
}

export interface Analysis {
  totals: Totals;
  /** One row per model and effort, every kind of work added together — most expensive first. */
  pairs: PairRow[];
  kinds: KindRow[];
  /** Every day of the period, in order, zero where nothing happened. */
  daily: DayPoint[];
  /** The buckets as they are, for the findings that need the kind and the pair together. */
  buckets: { model: string; effort: string; kind: StepKind; bucket: UsageBucket }[];
}

export function zeroTotals(): Totals {
  return {
    turns: 0,
    input: 0,
    output: 0,
    cacheRead: 0,
    cacheWrite: 0,
    cost: 0,
    activeMs: 0,
    started: 0,
    landed: 0,
    failed: 0,
    stopped: 0,
    reruns: 0,
    stepsPassed: 0,
    stepsFailed: 0,
    stepReruns: 0,
    conflicts: 0,
    landMs: 0,
    landCount: 0
  };
}

function addBucket(into: Totals, kind: StepKind, b: UsageBucket): void {
  into.turns += b.t ?? 0;
  into.input += b.i ?? 0;
  into.output += b.o ?? 0;
  into.cacheRead += b.cr ?? 0;
  into.cacheWrite += b.cw ?? 0;
  into.cost += b.c ?? 0;
  into.activeMs += b.w ?? 0;
  if (kind === 'task') {
    into.started += b.s ?? 0;
    into.landed += b.l ?? 0;
    into.failed += b.f ?? 0;
    into.stopped += b.x ?? 0;
    into.reruns += b.r ?? 0;
  } else {
    into.stepsPassed += b.l ?? 0;
    into.stepsFailed += b.f ?? 0;
    into.stepReruns += b.r ?? 0;
  }
}

/** The totals of one day — or of a period, given the sum of its days. */
export function totalsOf(day: UsageDay): Totals {
  const out = zeroTotals();
  for (const [key, bucket] of Object.entries(day.b)) {
    const parsed = parseBucketKey(key);
    if (parsed) addBucket(out, parsed.kind, bucket);
  }
  out.conflicts = day.k ?? 0;
  out.landMs = day.tl ?? 0;
  out.landCount = day.tn ?? 0;
  return out;
}

export function tokensOf(t: Pick<Totals, 'input' | 'output' | 'cacheRead' | 'cacheWrite'>): number {
  return t.input + t.output + t.cacheRead + t.cacheWrite;
}

/** Landed over started, or null with nothing started. */
export function successRate(t: Pick<Totals, 'started' | 'landed'>): number | null {
  return t.started > 0 ? Math.min(1, t.landed / t.started) : null;
}

/** Micro-dollars per landed task, or null with none landed. */
export function costPerLanded(t: Pick<Totals, 'cost' | 'landed'>): number | null {
  return t.landed > 0 ? t.cost / t.landed : null;
}

/** Cache reads over every input token the model read. */
export function cacheHitRatio(t: Pick<Totals, 'input' | 'cacheRead' | 'cacheWrite'>): number | null {
  const all = t.input + t.cacheRead + t.cacheWrite;
  return all > 0 ? t.cacheRead / all : null;
}

/** Everything a period of days says, for `days` — the days of the period, in any order, gaps allowed. */
export function analyse(rows: readonly UsageDayRow[], from: string, to: string, allDays: readonly string[]): Analysis {
  const inRange = rows.filter((row) => row.day >= from && row.day <= to);
  const sum = sumDays(inRange.map((row) => row.data));
  const pairs = new Map<string, PairRow>();
  const kinds = new Map<StepKind, KindRow>();
  const buckets: Analysis['buckets'] = [];
  for (const [key, bucket] of Object.entries(sum.b)) {
    const parsed = parseBucketKey(key);
    if (!parsed) continue;
    buckets.push({ ...parsed, bucket });
    const pairKey = `${parsed.model}|${parsed.effort}`;
    const pair = pairs.get(pairKey) ?? { model: parsed.model, effort: parsed.effort, ...zeroTotals() };
    addBucket(pair, parsed.kind, bucket);
    pairs.set(pairKey, pair);
    const kind = kinds.get(parsed.kind) ?? { kind: parsed.kind, ...zeroTotals() };
    addBucket(kind, parsed.kind, bucket);
    kinds.set(parsed.kind, kind);
  }
  const byDay = new Map(inRange.map((row) => [row.day, totalsOf(row.data)]));
  const daily = allDays.map((day) => {
    const t = byDay.get(day);
    return {
      day,
      cost: t?.cost ?? 0,
      turns: t?.turns ?? 0,
      tokens: t ? tokensOf(t) : 0,
      landed: t?.landed ?? 0,
      started: t?.started ?? 0,
      reruns: t?.reruns ?? 0,
      conflicts: t?.conflicts ?? 0,
      landMs: t?.landMs ?? 0,
      landCount: t?.landCount ?? 0
    };
  });
  return {
    totals: totalsOf(sum),
    pairs: [...pairs.values()].sort((a, b) => b.cost - a.cost || b.turns - a.turns),
    kinds: [...kinds.values()].sort((a, b) => b.cost - a.cost),
    daily,
    buckets
  };
}

/* ── the words the pages print figures in ────────────────────────────────── */

export function formatUsd(micros: number): string {
  const dollars = micros / 1e6;
  if (dollars === 0) return '$0';
  if (dollars < 0.01) return '<$0.01';
  if (dollars < 100) return `$${dollars.toFixed(2)}`;
  return `$${Math.round(dollars).toLocaleString('en-US')}`;
}

export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1e6) return `${(n / 1e3).toFixed(n < 1e4 ? 1 : 0)}k`;
  if (n < 1e9) return `${(n / 1e6).toFixed(n < 1e7 ? 1 : 0)}M`;
  return `${(n / 1e9).toFixed(1)}B`;
}

export function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

export function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 90) return `${minutes} min`;
  const hours = ms / 3_600_000;
  return hours < 48 ? `${hours.toFixed(1)} h` : `${(hours / 24).toFixed(1)} days`;
}

/** `opus · xhigh`, or the model alone when it takes no effort. */
export function pairLabel(model: string, effort: string): string {
  return effort ? `${model} · ${effort}` : model;
}

export const KIND_LABELS: Record<StepKind, string> = {
  judge: 'Judgement steps (plan, review)',
  build: 'Implementation steps',
  mech: 'Mechanical steps (tests, checks, commits)',
  task: 'Outside a workflow step'
};

/** The same label in two halves — the name, and what it covers in a quieter note beside it. */
export function kindLabel(kind: StepKind): { label: string; sub: string | null } {
  const [label, rest] = KIND_LABELS[kind].split(' (');
  return { label, sub: rest ? rest.replace(/\)$/, '') : null };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `7 Sep` — or `7 Sep 2026` with `year`. */
export function formatShortDay(day: string, year = false): string {
  const date = new Date(`${day}T12:00:00Z`);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}${year ? ` ${date.getUTCFullYear()}` : ''}`;
}

/** `Tue 3 Oct` — a day key as a sentence names it. */
export function formatDay(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()];
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getUTCMonth()];
  return `${weekday} ${date.getUTCDate()} ${month}`;
}
